<template>
  <v-form ref="form" v-model:model-value="validated" :disabled="$loader.loading">
    <v-card class="pa-8">
      <v-card-title class="mb-4 d-flex align-center">
        <v-btn
          class="me-4"
          color="primary"
          size="small"
          icon="fa-solid fa-circle-1"
        />
        <span>
          Dados do Evento
        </span>
      </v-card-title>

      <v-row>
        <v-col cols="12" sm="4">
          <v-text-field
            v-model="model.title"
            density="compact"
            variant="outlined"
            label="Nome*"
            :rules="[rules.required]"
            hide-details
          />
        </v-col>
        <v-col cols="12" sm="4">
          <v-select
            v-model="model.category"
            density="compact"
            variant="outlined"
            label="Categoria*"
            :items="categories"
            item-title="title"
            item-value="id"
            :rules="[rules.required]"
            return-object
            hide-details
          />
        </v-col>
        <v-col cols="12" sm="4">
          <v-select
            v-model="model.biometric_providers"
            density="compact"
            variant="outlined"
            label="Provedor Biométrico"
            item-title="name"
            item-value="id"
            :items="biometricProviders"
            :disabled="biometricProviders.length === 0"
            return-object
            multiple
            chips
            clearable
            hide-details
          />
        </v-col>
        <v-col cols="12" sm="4">
          <v-select
            v-model="model.gateway"
            density="compact"
            variant="outlined"
            label="Provedor de Pagamento"
            item-title="name"
            item-value="id"
            :items="gateways"
            :disabled="gateways.length === 0"
            return-object
            clearable
            hide-details
          />
        </v-col>
        <v-col cols="12" sm="4">
          <v-select
            v-model="model.configuration"
            density="compact"
            variant="outlined"
            label="Configuração de Ingresso*"
            :item-title="c => `${c.id.split('-')[0]} - ${c.name}`"
            item-value="id"
            :items="configurations"
            :rules="[rules.required]"
            :disabled="isEdit"
            return-object
            hide-details
          />
        </v-col>
        <v-col cols="12" sm="4">
          <v-text-field
            v-model="model.insurance_policy"
            density="compact"
            variant="outlined"
            label="Apólice"
            hide-details
          />
        </v-col>
      </v-row>
      <v-row>
        <v-col cols="12">
          <text-editor v-model="model.description" label="Descrição" />
        </v-col>
      </v-row>
      <v-row>
        <v-col cols="12" sm="4" lg="2">
          <date-time-field v-model="model.dates.open" :rules="[rules.required, rules.datetime('dd/MM/yyyy HH:mm:ss')]" label="Data de Abertura dos Portões*" />
        </v-col>
        <v-col cols="12" sm="4" lg="2">
          <date-time-field v-model="model.dates.open_display" :rules="[rules.datetime('dd/MM/yyyy HH:mm:ss')]" label="Data de Abertura dos Portões (Visual)" />
        </v-col>
        <v-col cols="12" sm="4" lg="2">
          <date-time-field v-model="model.dates.start" :rules="[rules.required, rules.datetime('dd/MM/yyyy HH:mm:ss')]" label="Data de Início*" />
        </v-col>
        <v-col cols="12" sm="4" lg="2">
          <date-time-field v-model="model.dates.end" :rules="[rules.required, rules.datetime('dd/MM/yyyy HH:mm:ss')]" label="Data de Fim*" />
        </v-col>
        <v-col cols="12" sm="4" lg="2">
          <v-select
            v-model="model.show_on_homepage"
            label="Exibir na Home*"
            density="compact"
            variant="outlined"
            :items="[{ title: 'Habilitado', value: true }, { title: 'Desabilitado', value: false }]"
          />
        </v-col>
        <v-col cols="12" sm="4" lg="2">
          <v-select
            v-model="model.show_on_whitelabel_page"
            label="Exibir na Página do Produtor*"
            density="compact"
            variant="outlined"
            :items="[{ title: 'Habilitado', value: true }, { title: 'Desabilitado', value: false }]"
          />
        </v-col>
      </v-row>
    </v-card>

    <v-card class="pa-8 mt-4">
      <v-card-title class="mb-4 d-flex align-center">
        <v-btn
          class="me-4"
          color="primary"
          size="small"
          icon="fa-solid fa-circle-2"
        />
        <span>
          Dados da Venda
        </span>
      </v-card-title>

      <v-row>
        <v-col cols="12" md="2">
          <v-select
            v-model="model.printing_required"
            label="Impressão Obrigatória*"
            density="compact"
            variant="outlined"
            :items="[{ title: 'Habilitada', value: true }, { title: 'Desabilitada', value: false }]"
          />
        </v-col>
        <v-col cols="12" sm="4" md="2">
          <v-select
            v-model="model.online_sales"
            label="Venda Online*"
            density="compact"
            variant="outlined"
            :items="[{ title: 'Habilitada', value: true }, { title: 'Desabilitada', value: false }]"
          />
        </v-col>
        <v-col cols="12" sm="4" md="3">
          <date-time-field v-model="model.dates.sales.open" label="Abertura da Venda Online" />
        </v-col>
        <v-col cols="12" sm="4" md="3">
          <date-time-field v-model="model.dates.sales.close" label="Encerramento da Venda Online" />
        </v-col>
        <v-col cols="12" sm="4" md="2">
          <v-text-field
            v-model="model.max_online_tickets"
            label="Ingressos Online por Pessoa"
            density="compact"
            variant="outlined"
            type="number"
          />
        </v-col>
        <v-col cols="12" sm="8" md="4">
          <v-select
            v-model="model.payment_methods"
            multiple
            return-object
            chips
            clearable
            label="Métodos de Pagamento"
            :items="methods"
            item-title="name"
            item-value="id"
            density="compact"
            variant="outlined"
          />
        </v-col>
        <v-col cols="12" sm="6" md="2">
          <v-select
            v-model="model.convenience_fee_mode"
            label="Tipo de Taxa*"
            density="compact"
            variant="outlined"
            :items="[
              { title: 'Valor Fixo', value: 'absolute' },
              { title: 'Percentual', value: 'percentual' },
            ]"
          />
        </v-col>
        <v-col cols="12" sm="6" md="2">
          <v-text-field
            v-model="model.convenience_fee"
            :prepend-inner-icon="model.convenience_fee_mode === 'absolute' ? 'fa-fw fa-regular fa-money-bill' : 'fa-percent'"
            label="Valor da Taxa*"
            density="compact"
            variant="outlined"
            type="number"
            :rules="[rules.required]"
          />
        </v-col>
        <v-col cols="12" sm="6" md="2">
          <v-select
            v-model="model.document_required"
            label="CPF Obrigatório*"
            density="compact"
            variant="outlined"
            :items="[{ title: 'Habilitado', value: true }, { title: 'Desabilitado', value: false }]"
          />
        </v-col>
        <v-col cols="12" sm="6" md="2">
          <v-select
            v-model="model.name_required"
            label="Nome Obrigatório*"
            density="compact"
            variant="outlined"
            :items="[{ title: 'Habilitado', value: true }, { title: 'Desabilitado', value: false }]"
          />
        </v-col>
      </v-row>
    </v-card>

    <form-event-images v-model="images" :event="model" />

    <form-event-sectors
      v-model="sectorConfigs"
      :sectors="sectors"
      :event="model"
    />

    <form-event-settings v-model="model.settings" />

    <v-row justify="end" class="mt-4">
      <v-col cols="2">
        <v-btn
          size="large"
          block
          color="primary"
          :loading="$loader.loading"
          @click="submit"
        >
          Salvar
        </v-btn>
      </v-col>
    </v-row>
  </v-form>
</template>

<script lang="ts" setup>
import DateTimeField from '@/components/form/DateTimeField.vue'
import TextEditor from '@/components/text-editor/TextEditor.vue'
import PaymentMethodService from '@/services/PaymentMethodService'
import { useSnackBar } from '@/stores/snackbar'
import Event from '@/types/Event'
import PaymentMethod from '@/types/PaymentMethod'
import { computed, ref, Ref, watch } from 'vue'
import FormEventImages, { ImageState } from './partials/form-event-images.vue'
import FormEventSectors, { SectorConfigState } from './partials/form-event-sectors.vue'
import FormEventSettings from './partials/form-event-settings.vue'
import EventConfigurationService from '@/services/EventConfigurationService'
import MappingService from '@/services/MappingService'
import Sector from '@/types/Sector'
import EventConfiguration from '@/types/EventConfiguration'
import { useLoader } from '@/stores/loader'
import CategoryService from '@/services/CategoryService'
import { Category } from '@/types/Category'
import { datetime } from '@/helpers/rules/datetime'
import { required } from '@/helpers/rules/required'
import BiometricProviderService from '@/services/BiometricProviderService'
import { useProducerStore } from '@/stores/producer'
import { BiometricProvider } from '@/types/BiometricProvider'
import { DateTime } from 'luxon'
import PaymentGateway from '@/types/PaymentGateway'
import PaymentGatewayService from '@/services/PaymentGatewayService'

interface Props {
  event: Event
}

const snackbar = useSnackBar()
const loader = useLoader()

const rules = {
  required,
  datetime,
}

const emit = defineEmits< { submit: [ model: Event, images: ImageState[], sectorConfigs: SectorConfigState[] ] }>()

const props = defineProps<Props>()

const model: Ref<Event> = ref(props.event)
const images: Ref<ImageState[]> = ref([])
const sectorConfigs: Ref<SectorConfigState[]> = ref([])
const sectors: Ref<Sector[]> = ref([])
const methods: Ref<PaymentMethod[]> = ref([])
const configurations: Ref<EventConfiguration[]> = ref([])
const categories: Ref<Category[]> = ref([])
const biometricProviders: Ref<BiometricProvider[]> = ref([])
const gateways: Ref<PaymentGateway[]> = ref([])

const validated = ref(true)

const isEdit = computed(() => !!model.value.id)

async function fetchPaymentMethods() {
  try {
    loader.increment()
    const service = new PaymentMethodService()

    const response = await service.index(1, '', 15)

    const items = response.data.data

    methods.value.push(...items.filter(i => i.show))
  } catch {
    snackbar.show('Erro ao carregar métodos de pagamento', 'error')
  } finally {
    loader.decrement()
  }
}

async function fetchEventConfigurations() {
  try {
    loader.increment()
    const service = new EventConfigurationService()

    const response = await service.index(1, 'active=true', 100)

    const items = response.data.data
      .filter(i => i.active)
      .sort((a, b) => a.name.localeCompare(b.name))

    configurations.value.push(...items)
  } catch {
    snackbar.show('Erro ao carregar configurações de evento', 'error')
  } finally {
    loader.decrement()
  }
}

async function fetchCategories() {
  try {
    loader.increment()

    const service = new CategoryService()

    const response = await service.index()

    const items = response.data.data

    categories.value = items.sort((a, b) => a.title.localeCompare(b.title))
  } catch {
    snackbar.show('Erro ao carregar categorias', 'error')
  } finally {
    loader.decrement()
  }
}

async function fetchBiometricProviders() {
  try {
    loader.increment()
    const service = new BiometricProviderService()

    const response = await service.index(1, `&producer=${useProducerStore().producer?.id}`, 100)

    biometricProviders.value = response.data.data
  } catch {
    snackbar.show('Erro ao carregar provedores biométricos', 'error')
  } finally {
    loader.decrement()
  }
}

async function fetchPaymentGateways() {
  try {
    const service = new PaymentGatewayService()

    const response = await service.index()

    gateways.value = response.data.data
  } catch {
    snackbar.show('Erro ao carregar provedores de pagamento', 'error')
  }
}

async function fetchSectors() {
  const mappingId = model.value.configuration?.mapping_id
  if (!mappingId) return

  try {
    loader.increment()
    const response = await new MappingService().show(mappingId)
    sectors.value = response.data.data.sectors ?? []
  } catch {
    snackbar.show('Erro ao carregar setores', 'error')
  } finally {
    loader.decrement()
  }
}

function submit() {
  emit('submit', model.value, images.value, sectorConfigs.value)
}

fetchPaymentMethods()
fetchCategories()
fetchBiometricProviders()
fetchPaymentGateways()

if (!isEdit.value) {
  fetchEventConfigurations()
}

watch(() => model.value.configuration?.mapping_id, fetchSectors, { immediate: true })

// Auto fill the event dates
watch(
  () => model.value.dates.open,
  (value) => {
    if (!value) return

    const datetime = DateTime.fromFormat(value, 'yyyy-MM-dd HH:mm:ss')

    if (datetime.isValid) {
      const dates = model.value.dates
      if (!dates.open_display) dates.open_display = datetime.toFormat('yyyy-MM-dd HH:mm:ss')
      if (!dates.start) dates.start = datetime.toFormat('yyyy-MM-dd HH:mm:ss')
      if (!dates.end) dates.end = datetime.set({ hour: 23, minute: 59, second: 59 }).toFormat('yyyy-MM-dd HH:mm:ss')
    }
  },
)
</script>
